import { useEffect, useState } from "react";
import { useLanguage } from "@/i18n/LanguageContext";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { fetchPublicCompanies } from "@/lib/api";

interface Company {
  id: string;
  name: string;
  type: string;
  type_label: string;
  logo_url?: string | null;
}

export const CompaniesSection = () => {
  const { t } = useLanguage();
  const [companies, setCompanies] = useState<Company[]>([]);

  useEffect(() => {
    fetchPublicCompanies()
      .then((r) => setCompanies(r.data.companies || []))
      .catch(() => setCompanies([]));
  }, []);

  if (companies.length === 0) return null;

  return (
    <section
      id="companies"
      data-testid="companies-section"
      className="border-t border-white/10 bg-ink py-24 md:py-32"
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <Reveal>
          <SectionLabel text={t.companies.label} testId="companies-label" />
          <h2
            data-testid="companies-heading"
            className="mt-6 font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-white md:text-6xl"
          >
            {t.companies.heading}
          </h2>
          <p className="mt-5 max-w-xl text-base leading-relaxed text-neutral-400">
            {t.companies.sub}
          </p>
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {companies.map((c, i) => (
            <Reveal key={c.id} delay={i * 0.08}>
              <div
                data-testid={`company-card-${c.id}`}
                className="group flex h-full flex-col items-start justify-between border border-white/10 bg-charcoal p-8 transition-transform duration-500 hover:-translate-y-1 md:p-10"
              >
                {c.logo_url ? (
                  <img
                    src={c.logo_url}
                    alt={`${c.name} logo`}
                    className="h-10 w-auto object-contain"
                  />
                ) : (
                  <p className="font-display text-2xl font-black tracking-tight text-white md:text-3xl">
                    {c.name}
                  </p>
                )}
                <span className="mt-6 rounded-full border border-gold/40 bg-gold/10 px-4 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-gold">
                  {t.companies.types[c.type] || c.type_label}
                </span>
                <span className="mt-6 block h-[3px] w-8 bg-gold transition-[width] duration-500 group-hover:w-full" />
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
